// © Microsoft Corporation. All rights reserved.

import * as React from "react";

import { ArticleCard, ArticleProviderProps } from "@msnews/article-card";
import {
    DialogArticleCardHandledProps,
    DialogArticleCardManagedClasses,
    DialogArticleCardUnhandledProps
} from "./DialogArticleCard.props";
import { HandledPropsKeys, ImageResizeMode, generateImageUrlFromBaseUrl } from "@msnews/core";
import { HeadingProps, HeadingSize, HeadingTag } from "@microsoft/fast-components-react-msft";
import { TypographyProps, TypographySize, TypographyTag } from "@microsoft/fast-components-react-base";

import Foundation from "@microsoft/fast-components-foundation-react";

/**
 * Dialog article card
 * @class - Dialog Article Card
 * @classdesc - An dialog article Card composed from the base article card.
 */
export class DialogArticleCard extends Foundation<DialogArticleCardHandledProps & DialogArticleCardManagedClasses, DialogArticleCardUnhandledProps, {}> {
    /**
     * Handled props contract
     * @protected
     * @type { HandledPropsKeys<DialogArticleCardHandledProps & DialogArticleCardManagedClasses> }
     */
    protected handledProps: HandledPropsKeys<DialogArticleCardHandledProps & DialogArticleCardManagedClasses> = {
        anchorRef: undefined,
        articleId: undefined,
        headlineTitle: undefined,
        href: undefined,
        image: undefined,
        managedClasses: undefined,
        provider: undefined
    };

    /**
     * Constructs an DialogArticleCard component
     * @constructor
     * @param { DialogArticleCardHandledProps & DialogArticleCardManagedClasses } props props contract
     */
    constructor(props: DialogArticleCardHandledProps & DialogArticleCardManagedClasses) {
        super(props);
    }

    /**
     * Component rendered
     * @method
     * @returns {JSX.Element} the DialogArticleCard component
     */
    public render(): JSX.Element {
        const { href, image, managedClasses } = this.props;

        if (image && image.src) {
            image.src = generateImageUrlFromBaseUrl(
                image.src,
                {
                    width: 100,
                    height: 100,
                    enableDpiScaling: false,
                    mode: ImageResizeMode.Scale
                });
        }

        const articleCard = (
            <ArticleCard
                {...this.unhandledProps()}
                managedClasses={managedClasses}
                headlineTitle={this.coerceTitleProps()}
                href={href}
                image={image}
                provider={this.coerceProviderProps()}
            />
        );

        if (image) {
            return (
                articleCard
            );
        } else {
            return (
                <div className={this.props.managedClasses.articleCard_noImage} >
                    {articleCard}
                </div>
            );
        }
    }

    /**
     * Coerces the title props to enforce visual level
     * @method
     * @returns {HeadingProps} the headline title props
     */
    private coerceTitleProps(): any {
        const coercedProps: Partial<HeadingProps> = {
            size: HeadingSize._6,
            tag: HeadingTag.h3
        };

        return { ...this.props.headlineTitle, ...coercedProps };
    }

    /**
     * Coerces the provider props to enforce visual level of the name
     * @method
     * @returns {ArticleProviderProps} the provider props
     */
    private coerceProviderProps(): ArticleProviderProps {
        const { provider } = this.props;
        if (!provider) {
            return;
        }

        const coercedProviderNameProps: Partial<TypographyProps> = {
            size: TypographySize._8,
            tag: TypographyTag.h5
        };

        const coercedProps: Partial<ArticleProviderProps> = {
            name: { ...provider.name, ...coercedProviderNameProps }
        };

        const coerceProviderProps = { ...provider, ...coercedProps };
        if (!coerceProviderProps.image || !coerceProviderProps.image.src) {
            coerceProviderProps.image = undefined;
        }

        return coerceProviderProps;
    }
}

export * from "./DialogArticleCard.props";